/**
 * Contrapartes bancárias do fornecedor: os CPF/CNPJ pelos quais o banco o
 * identifica num evento do extrato. O CNPJ do cadastro já casa sozinho; aqui
 * ficam os outros (CPF da sócia, outra filial) — aprendidos ao vincular um
 * evento na conciliação Itaú ou cadastrados à mão.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Landmark, Plus, Trash2 } from 'lucide-react';
import { PermissionGuard } from '@components/PermissionGuard';
import { providerService } from '../../services/providerService';
import type { ProviderBankCounterpart } from '../../types/provider';

const formatDocument = (digits: string): string => {
  if (digits.length === 11) return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  if (digits.length === 14) return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return digits;
};

const SOURCE_LABEL: Record<string, string> = {
  learned: 'aprendida na conciliação',
  manual: 'cadastrada',
};

export const ProviderBankCounterparts: React.FC<{ providerId: number }> = ({ providerId }) => {
  const [items, setItems] = useState<ProviderBankCounterpart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [document, setDocument] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await providerService.getBankCounterparts(providerId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar contrapartes bancárias');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    if (!document.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await providerService.addBankCounterpart(providerId, { document: document.trim(), name: name.trim() || undefined });
      setDocument('');
      setName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao cadastrar contraparte');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: ProviderBankCounterpart) => {
    try {
      setSaving(true);
      setError(null);
      await providerService.removeBankCounterpart(providerId, c.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao remover contraparte');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Landmark size={16} className="text-emerald-600" />
        <p className="text-xs text-slate-500 uppercase tracking-wider">Contrapartes bancárias</p>
      </div>
      <p className="text-xs text-slate-400 mb-3">
        Documentos pelos quais o banco identifica este fornecedor no extrato (além do CNPJ/CPF do cadastro). A
        conciliação Itaú usa esta lista para sugerir o fornecedor de um débito.
      </p>
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {loading ? (
        <p className="text-sm text-slate-400">Carregando...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">Nenhuma contraparte além do documento do cadastro.</p>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {items.map(c => (
            <li key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="font-mono text-slate-900">{formatDocument(c.document)}</span>
              <span className="text-slate-600 min-w-0 flex-1 truncate">{c.name || '—'}</span>
              <span className="text-[11px] text-slate-400 whitespace-nowrap">{SOURCE_LABEL[c.source] ?? c.source}</span>
              <PermissionGuard permission="providers.write">
                <button
                  type="button"
                  className="p-1 text-slate-400 hover:text-red-500 disabled:opacity-50"
                  title="Remover contraparte"
                  disabled={saving}
                  onClick={() => void remove(c)}
                >
                  <Trash2 size={14} />
                </button>
              </PermissionGuard>
            </li>
          ))}
        </ul>
      )}
      <PermissionGuard permission="providers.write">
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <input
            className="input text-sm sm:w-48"
            placeholder="CPF ou CNPJ"
            value={document}
            onChange={e => setDocument(e.target.value)}
          />
          <input
            className="input text-sm flex-1"
            placeholder="Nome como aparece no extrato (opcional)"
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <button
            type="button"
            className="inline-flex items-center justify-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            disabled={saving || !document.trim()}
            onClick={() => void add()}
          >
            <Plus size={14} /> Adicionar
          </button>
        </div>
      </PermissionGuard>
    </div>
  );
};

export default ProviderBankCounterparts;
